// "Other campaigns" grid on a combined campaign page (combined.docx, design:
// combined.png): one card per member campaign — image, title, its OWN progress
// (not the aggregate) — with two buttons under it, both into that campaign's
// own page. Boxes are ordered by progress %, highest first. Renders nothing on an ordinary campaign, whose record carries no
// sub_campaigns.

// The displayed progress percentage, uncapped — shared by the hero bar in
// CampaignPage.js and the boxes below. Rounded UP below 90% (a campaign at 44.2%
// is doing at least 45%'s worth of work, and the optimism costs nothing at that
// distance) but DOWN above it: near the goal the rounding is the message. 99.6%
// ceilinged to "100%" tells donors the campaign is funded and there is nothing
// left to give to, which is both untrue and the most expensive moment to be
// wrong. Down there, and only there, an understatement is the honest direction.
export function displayPct(raised, goal) {
  const exact = goal > 0 && raised != null ? (raised / goal) * 100 : 0;
  return exact > 90 ? Math.floor(exact) : Math.ceil(exact);
}

// Gramsci money is a numeric string ("50000.00"); null when absent.
const money = (v) => {
  const n = v != null ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.ceil(n) : null;
};

// A box's goal and raised amount, and whether it shows a bar at all.
function progressOf(c) {
  const goal = money(c.target_amount);
  const raised = money(c.total_raised_shekels);
  const showBar = c.show_goal !== false && c.show_total_raised !== false && goal > 0 && raised != null;
  return { goal, raised, showBar };
}

// Sort key for the grid: exact progress, highest first. A box without a bar has
// no percentage to be ranked by, so it sorts after every box that has one.
const sortKey = (c) => {
  const { goal, raised, showBar } = progressOf(c);
  return showBar ? raised / goal : -1;
};

function CampaignBox({ c }) {
  const href = `/${c.url_campaign_name}`;
  const { goal, raised, showBar } = progressOf(c);
  const rawPct = showBar ? displayPct(raised, goal) : 0;
  const pct = Math.min(100, rawPct);
  const image = /^(https?:\/\/|\/)/.test(c.image || '') ? c.image : null;

  return (
    <li className="cmp-other">
      <a className="cmp-other-card" href={href}>
        {image && <img className="cmp-other-img" src={image} alt="" loading="lazy" />}
        <div className="cmp-other-body">
          <h3 className="cmp-other-title">
            <span className="bi-ar">{c.title_ar || c.title_he}</span>
            <span className="bi-he">{c.title_he}</span>
          </h3>
          {showBar && (
            <div
              className="cmp-other-progress"
              role="progressbar"
              aria-valuenow={rawPct}
              aria-valuemin={0}
              aria-valuemax={Math.max(100, rawPct)}
              aria-valuetext={`${rawPct}%`}
            >
              {/* Wide enough, the label rides inside the fill (white); otherwise
                  it sits on the track just past the fill's leading edge. */}
              <div className="cmp-other-fill" style={{ width: `${pct}%` }}>
                {pct > 20 && <span className="cmp-other-pct cmp-other-pct--inside">{rawPct}%</span>}
              </div>
              {pct <= 20 && (
                <span className="cmp-other-pct" style={{ right: `calc(${pct}% + 6px)` }}>
                  {rawPct}%
                </span>
              )}
            </div>
          )}
        </div>
      </a>
      <a className="cmp-cta cmp-other-cta" href={href}>
        <span className="bi-he">תרמו עכשיו</span>{' '}
        <span className="bi-ar">تبرّعوا الآن</span>
      </a>
      <a className="cmp-who cmp-other-more" href={href}>
        <span className="bi-he">לפרטים נוספים</span>{' '}
        <span className="bi-ar">لمزيد من التفاصيل</span>
      </a>
    </li>
  );
}

// `className` adds a placement modifier (the in-card mobile copy uses it).
export default function OtherCampaigns({ campaigns, className = '' }) {
  if (!Array.isArray(campaigns) || campaigns.length === 0) return null;
  // Array sort is stable, so ties keep the server's memberSlugs order.
  const sorted = [...campaigns].sort((a, b) => sortKey(b) - sortKey(a));
  return (
    <section className={`cmp-others${className ? ` ${className}` : ''}`}>
      <h2 className="cmp-others-title">
        <span className="bi-he">תרמו למטה מקומי</span>{' '}
        <span className="bi-ar">تبرّعوا لمقرّ محلي</span>
      </h2>
      <ul className="cmp-others-grid">
        {sorted.map((c) => (
          <CampaignBox key={c.url_campaign_name} c={c} />
        ))}
      </ul>
    </section>
  );
}
